import { apiErrorMessage } from '../../../../utils/apiError';
import { DmShellLayout } from '../dm/DmShellLayout';
import { SkeletonCard } from '../dm/DmSkeletons';
import { ErrorBanner } from '../dm/DmBanners';
import type { ActionRunnerProps } from '../types';
import { OrderPreview } from './orthopulse/OrderPreview';
import { OrthopulseOrderNotice } from '../../../shared/OrthopulseBlockedBanner';
import { SuccessStep } from './orthopulse/SuccessStep';
import { ForceOverridePanel } from './orthopulse/ForceOverridePanel';
import { ShipToStepCard } from './orthopulse/ShipToStepCard';
import {
    useOrthopulseOrderFlow,
    type UseOrthopulseOrderFlowResult
} from './orthopulse/useOrthopulseOrderFlow';

interface PatientSummaryCardProps {
    patientName: string;
}

function PatientSummaryCard({ patientName }: PatientSummaryCardProps) {
    return (
        <div className="bg-white rounded-xl border border-gray-200 p-6">
            <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">
                Patient
            </div>
            <div className="text-base font-semibold text-gray-900">{patientName}</div>
        </div>
    );
}

interface OrderOrthopulseFlowContentProps {
    flow: UseOrthopulseOrderFlowResult;
    patientName: string;
}

// The pre-success body: patient card, initial-load state, blocked banner +
// override panel, then whichever of step 1/2 is active.
function OrderOrthopulseFlowContent({ flow, patientName }: OrderOrthopulseFlowContentProps) {
    return (
        <div className="w-full max-w-[1100px] mx-auto flex flex-col gap-4">
            <PatientSummaryCard patientName={patientName} />

            {flow.initialPreview.isLoading && <SkeletonCard />}

            {flow.initialPreview.isError && (
                <ErrorBanner
                    message={apiErrorMessage(
                        flow.initialPreview.error,
                        'Failed to load order details'
                    )}
                    onRetry={() => void flow.initialPreview.refetch()}
                />
            )}

            {flow.step === 'ship-to' && flow.initialPreview.data && (
                <OrthopulseOrderNotice
                    ordersVia={flow.initialPreview.data.ordersVia}
                    blocked={flow.initialPreview.data.blocked}
                    practiceCharged={flow.initialPreview.data.practiceCharged}
                />
            )}

            {flow.forceOverride.canShow && (
                <ForceOverridePanel
                    open={flow.forceOverride.open}
                    onOpen={flow.forceOverride.openPanel}
                    onCancel={flow.cancelForceOverride}
                    reason={flow.forceOverride.reason}
                    onReasonChange={flow.forceOverride.setReason}
                />
            )}

            {flow.initialPreview.data && flow.step === 'ship-to' && <ShipToStepCard flow={flow} />}

            {flow.step === 'preview' && flow.previewWithShipTo.data && (
                <OrderPreview
                    preview={flow.previewWithShipTo.data}
                    sendOrder={flow.sendOrder}
                    onSend={flow.handleSend}
                    onBack={flow.goToShipTo}
                    isForcing={flow.isForcing}
                />
            )}
        </div>
    );
}

function OrderOrthopulseAction({
    patientId,
    patientName,
    onBack,
    onDataChange
}: ActionRunnerProps) {
    const flow = useOrthopulseOrderFlow(patientId, onDataChange);

    if (flow.sendOrder.isSuccess) {
        return (
            <DmShellLayout title="Order OrthoPulse" onBack={onBack}>
                <SuccessStep
                    patientName={patientName}
                    poNumber={flow.sendOrder.data.poNumber}
                    recipients={flow.sendOrder.data.recipients}
                    onBack={onBack}
                />
            </DmShellLayout>
        );
    }

    return (
        <DmShellLayout title="Order OrthoPulse" onBack={onBack}>
            <OrderOrthopulseFlowContent flow={flow} patientName={patientName} />
        </DmShellLayout>
    );
}

export { OrderOrthopulseAction };
